"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { FadeIn } from "@/components/global/utils/FadeIn";
import { INewTraderItem } from "@/sanity/pages/PageHomeTemplate";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";
import {
  CandleGraphic1,
  CandleGraphic2,
  CandleGraphic3,
  CandleGraphic4,
} from "./_candles";

interface NewTradersSectionProps {
  chip?: string;
  title?: PortableTextBlock[];
  items?: INewTraderItem[];
}

const ChevronLeft = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="w-[3rem] h-[3rem] shrink-0"
    aria-hidden
  >
    <path
      d="M15 6l-6 6 6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRight = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="w-[3rem] h-[3rem] shrink-0"
    aria-hidden
  >
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Big featured card (Figma "Matteo Conti"): blue gradient bg, decorative candle
// graphics with mix-blend-overlay, blurred name word art behind the portrait,
// portrait on the right, eyebrow upper-left, and a micro-print bio at the
// bottom rendered at 4px (decorative noise, intentional per design).
function FeaturedCard({ trader }: { trader: INewTraderItem }) {
  return (
    <div
      className="overflow-clip relative shrink-0 rounded-[1rem] w-[45.25rem] h-[48.75rem] lg:w-[95.25rem] lg:h-[59.25rem]"
      style={{
        backgroundImage:
          "linear-gradient(180.056deg, #0C1E29 13.038%, #314C5E 120.36%)",
      }}
    >
      {/* Candle graphics — scattered around the card periphery, mostly bleeding
          off the edges with mix-blend-overlay; the corners just catch subtle
          ticker patterns. Insets resolve against the card (no extra wrapper). */}
      <div className="absolute inset-0 mix-blend-overlay pointer-events-none">
        <div className="absolute inset-[86.09%_110.24%_-28.8%_-68.9%] opacity-[0.48]">
          <CandleGraphic1 className="w-full h-full shrink-0" />
        </div>
        <div className="absolute inset-[46.82%_51.26%_10.47%_-9.91%] opacity-[0.48]">
          <CandleGraphic2 className="w-full h-full shrink-0" />
        </div>
        <div className="absolute inset-[6.78%_-7.95%_50.51%_49.29%] opacity-[0.48]">
          <CandleGraphic3 className="w-full h-full shrink-0" />
        </div>
        <div className="absolute inset-[-32.49%_-66.93%_89.77%_108.28%] opacity-[0.48]">
          <CandleGraphic4 className="w-full h-full shrink-0" />
        </div>
      </div>

      {/* Word art — name as a soft white glow behind the portrait. Anchored
          off-left so it bleeds rightward, partially behind the photo (Figma:
          left calc(50% - 353px) ≈ left 28px on a 762-wide card). Prefer a
          pre-styled PNG from Sanity; fall back to text using Impact / Arial
          Narrow Bold (closest free approximations of Humane). */}
      {trader.nameImage ? (
        <Image
          src={trader.nameImage.src}
          alt={trader.name ?? ""}
          width={trader.nameImage.width}
          height={trader.nameImage.height}
          placeholder={trader.nameImage.lqip ? "blur" : undefined}
          blurDataURL={trader.nameImage.lqip}
          className="absolute object-contain object-left w-[24rem] left-[0.5rem] top-[10rem] h-[10.5rem] lg:left-[1rem] lg:top-[19.105rem] lg:w-[55rem] lg:h-auto"
        />
      ) : (
        trader.name && (
          <p
            style={{
              fontFamily:
                '"Impact","Haettenschweiler","Arial Narrow Bold",sans-serif',
            }}
            className="m-0 absolute whitespace-nowrap uppercase font-bold text-white left-[1.625rem] top-[11.685rem] text-[10.5rem] leading-[0.9] tracking-[-0.25rem] blur-[0.6rem] lg:left-[3.75rem] lg:top-[19.105rem] lg:text-[24rem] lg:tracking-[-0.5rem] lg:blur-[1.27rem]"
          >
            {trader.name}
          </p>
        )
      )}

      {/* Portrait — sits on the right half. Per Figma it's much taller than the
          card (704px on a 474px card) so the bottom is cropped by overflow-clip,
          giving the photo a "bleeds off the bottom" feel. */}
      {trader.image && (
        <div className="absolute top-[0.56rem] right-[0rem] w-[30rem] h-full lg:top-[1rem] lg:right-[1rem] lg:w-[46.933rem] lg:h-[60rem]">
          <Image
            quality={100}
            src={trader.image}
            alt={trader.name ?? ""}
            fill
            className="object-cover !w-full !h-full"
          />
        </div>
      )}

      {/* Eyebrow — Figma centers it vertically on y=133px (lg:top-16.625rem
          with -translate-y-1/2). On mobile, top-anchored for readability. */}
      <p className="m-0 absolute uppercase text-white/50 leading-[1.4] font-geist text-[1.25rem] top-[8.875rem] left-[1.625rem] -translate-y-1/2 lg:text-[1.75rem] lg:top-[16.625rem] lg:left-[3.75rem]">
        // new trader
      </p>

      {/* Bio — decorative micro-print at the bottom. Figma uses 4px font size
          (≈ 0.5rem here) which is intentional noise; some browsers clamp to a
          minimum. Centered horizontally and vertically on y=445px. */}
      {trader.bio && (
        <p className="m-0 absolute left-1/2 uppercase text-center font-geist text-white/50 leading-[1.4] -translate-x-1/2 -translate-y-1/2 w-[40.375rem] text-[0.5rem]  lg:w-[88.125rem] bottom-[1rem] lg:text-[0.65rem]">
          {trader.bio}
        </p>
      )}
    </div>
  );
}

// Compact preview card (Figma "Market maker" / "Headgefonds Manager"):
// portrait or graphic as full-bleed background with a mix-blend-hue radial
// gradient overlay, a thin linear gradient layer, eyebrow and category title
// pinned to the bottom-left.
function PreviewCard({ trader }: { trader: INewTraderItem }) {
  return (
    <div className="overflow-clip relative shrink-0 rounded-[1rem] bg-black border border-transparent w-[45.25rem] h-[48.75rem] lg:w-[42.625rem] lg:h-[59.5rem]">
      {trader.image && (
        <Image
          src={trader.image}
          alt=""
          fill
          className="object-cover object-top"
        />
      )}
      <div
        className="absolute inset-0 mix-blend-hue pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 30% 50%, rgba(31,55,72,1) 0%, rgba(48,74,92,1) 25%, rgba(65,93,113,1) 50%, rgba(99,131,153,1) 100%)",
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(180.126deg, rgba(12,30,41,0.25) 13.038%, rgba(49,76,94,0.35) 120.36%)",
        }}
      />
      <div className="absolute flex flex-col items-start left-[2.625rem] top-[39.375rem] w-[37.125rem] gap-[1.25rem] lg:left-[2.875rem] lg:top-[49.875rem]">
        <p className="m-0 font-geist uppercase text-white/50 leading-[1.4] text-[1.5rem]">
          // next new trader
        </p>
        <p className="m-0 font-news leading-[0.9] text-white text-[3.75rem] tracking-[-0.15rem]">
          {trader.category}
        </p>
      </div>
    </div>
  );
}

export default function NewTradersSection({
  chip,
  title,
  items,
}: NewTradersSectionProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  // Mirror Embla's state into React so the custom dots/arrows reflect the
  // active snap. `select` fires on every settled scroll; `reInit` covers
  // window resizes that recompute snap positions.
  useEffect(() => {
    if (!api) return;
    const sync = () => {
      setCurrent(api.selectedScrollSnap());
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };
    sync();
    api.on("select", sync);
    api.on("reInit", sync);
    return () => {
      api.off("select", sync);
      api.off("reInit", sync);
    };
  }, [api]);

  if (!items || items.length === 0) return null;

  return (
    <section className="relative w-full bg-black overflow-x-clip pb-[8rem] lg:pb-[15rem]">
      {/* Header */}
      <div className="flex flex-col items-center mx-auto text-center gap-[3rem] px-[2rem] w-[min(150rem,100%)]">
        {chip && (
          <FadeIn delay={0}>
            <p className="m-0 uppercase font-jetbrainsmono tracking-[0.02rem] text-[#B8D2E2] text-[1.75rem]">
              <span className="text-[#B8D2E2]/20">|</span>
              <span className="text-[#B8D2E2]/40">|</span>
              <span className="text-[#B8D2E2]/60">|</span>
              <span className="text-[#B8D2E2]/80"> {chip} </span>
              <span className="text-[#B8D2E2]/60">|</span>
              <span className="text-[#B8D2E2]/40">|</span>
              <span className="text-[#B8D2E2]/20">|</span>
            </p>
          </FadeIn>
        )}
        {title && (
          <FadeIn
            delay={0.1}
            className="richtext richtext-center prose-headings:m-0 prose-headings:font-news prose-headings:leading-[1.1] prose-headings:text-white/90 prose-headings:text-[4.5rem] prose-headings:tracking-[-0.15rem] lg:prose-headings:text-[6rem] lg:prose-headings:tracking-[-0.25rem] prose-p:m-0 prose-p:font-news prose-p:leading-[1.1] prose-p:text-white/90 prose-p:text-[4.5rem] prose-p:tracking-[-0.15rem] lg:prose-p:text-[6rem] lg:prose-p:tracking-[-0.25rem]"
          >
            <PortableText value={title} components={richTextComponent} />
          </FadeIn>
        )}
      </div>

      {/* Carousel area — wrapper spans the section so the right-edge gradient
          can sit flush with the viewport (above the Embla viewport's cards).
          The carousel itself stays in the safe area; viewport extended via
          negative right margin on desktop so cards bleed into the right gutter. */}
      <div className="relative mt-[3rem] lg:mt-[5rem]">
        <div className="mx-auto pl-[2rem] lg:pl-0 w-[min(150rem,100%)]">
          <Carousel
            setApi={setApi}
            opts={{ align: "start", loop: true }}
            className="lg:-mr-[50rem] w-full [&>div]:overflow-visible"
          >
            <CarouselContent className="-ml-[1.5rem]">
              {items.map((trader, i) => (
                <CarouselItem key={i} className="basis-auto pl-[1.5rem]">
                  {trader.featured ? (
                    <FeaturedCard trader={trader} />
                  ) : (
                    <PreviewCard trader={trader} />
                  )}
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>

        {/* Right fade — pinned to the section's right edge so it always reaches
            the viewport edge regardless of the safe area's width. */}
        <div className="hidden lg:block absolute top-0 right-0 bottom-0 w-[60rem] pointer-events-none bg-gradient-to-l from-black via-black/90 to-transparent z-10" />
        <div className="hidden lg:block absolute top-0 left-0 bottom-0 w-[60rem] pointer-events-none bg-gradient-to-r from-black via-black/90 to-transparent z-10" />
      </div>

      {/* Controls — 250×48 (31.25rem × 6rem) per Figma: solid white prev with
          dark chevron, dot row, glass next with white chevron. */}
      <div className="flex justify-between items-center mx-auto mt-[5rem] lg:mt-[7rem] h-[6rem] w-[min(49.75rem,calc(100%-4rem))] lg:w-[31.25rem]">
        <button
          type="button"
          onClick={() => api?.scrollPrev()}
          aria-label="Previous trader"
          disabled={!canPrev}
          className="flex justify-center items-center bg-white text-[#161616] rounded-full p-[1.5rem] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft />
        </button>
        <div className="flex items-center gap-[1.5rem]">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => api?.scrollTo(i)}
              aria-label={`Go to trader ${i + 1}`}
              className={`shrink-0 bg-white rounded-[0.25rem] transition-all duration-300 ${
                i === current
                  ? "w-[4rem] h-[1rem] opacity-100"
                  : "w-[1rem] h-[1rem] opacity-40"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => api?.scrollNext()}
          aria-label="Next trader"
          disabled={!canNext}
          className="flex justify-center items-center backdrop-blur-[2.5rem] bg-white/[0.09] border border-white/10 text-white rounded-full p-[1.5rem] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronRight />
        </button>
      </div>
    </section>
  );
}
